import React, { useCallback, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import DAIComboBox from '../../DAIComponents/Input/Combo/DAIComboBox'
import useAutomationContext from '../../hooks/useAutomationContext'
import DAISidePanel from '../../DAIComponents/SidePanel/DAISidePanel'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import DAITextBox from '../../DAIComponents/Input/Text/DAITextBox'
import { DAIRequest } from '../../utils/DAIRequest'
import { useQueryClient } from '@tanstack/react-query'
import { useNotifications } from '../../contexts/notificationContext'
import useTemplateContext from '../../hooks/useTemplateContext'
import DAIDateInput from '../../DAIComponents/Input/Date/DAIDateInput'

type NamedEntity = {
  id: string
  name: string
  type?: string
}

const automationFormSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  triggerType: z.string().min(1, 'Trigger type is required'),
  schedule: z.string().optional(),
  condition: z.object({
    type: z.string().min(1, 'Condition type is required'),
    field: z.string().min(1, 'Field is required'),
    operation: z.string().min(1, 'Operation is required'),
    value: z.string(),
    unit: z.string().optional(),
  }),
  action: z.object({
    type: z.string().min(1, 'Action type is required'),
    template: z.string().optional(),
  }),
})

type AutomationForm = z.infer<typeof automationFormSchema>

type FieldError = {
  message?: string
  type?: string
}

type FormErrors = {
  name?: FieldError
  triggerType?: FieldError
  schedule?: FieldError
  condition?: {
    type?: FieldError
    field?: FieldError
    operation?: FieldError
    value?: FieldError
    unit?: FieldError
  }
  action?: {
    type?: FieldError
    template?: FieldError
  }
}

const triggerTypes: NamedEntity[] = [
  { id: 'TIME_BASED', name: 'Time Based (Recurring)' },
  { id: 'DATE_BASED', name: 'Date Based (One-time)' },
  { id: 'EVENT_BASED', name: 'Event Based' },
]

const conditionTypes: NamedEntity[] = [
  { id: 'DATE', name: 'Date' },
  { id: 'NUMERIC', name: 'Number' },
  { id: 'STRING', name: 'Text' },
]

const conditionFields: NamedEntity[] = [
  { id: 'Contact.birthday', name: 'Contact Birthday', type: 'DATE' },
  { id: 'Contact.anniversary', name: 'Contact Anniversary', type: 'DATE' },
  { id: 'Contact.lastContactedAt', name: 'Last Contacted Date', type: 'DATE' },
  { id: 'Purchase.cost', name: 'Purchase Amount', type: 'NUMERIC' },
  { id: 'Contact.name', name: 'Contact Name', type: 'STRING' },
  { id: 'Contact.email', name: 'Contact Email', type: 'STRING' },
]

const operations: NamedEntity[] = [
  { id: 'EQUALS', name: 'Equals' },
  { id: 'GREATER_THAN', name: 'Greater Than' },
  { id: 'LESS_THAN', name: 'Less Than' },
]

const units: NamedEntity[] = [{ id: 'DAYS', name: 'Days' }]

const actionTypes: NamedEntity[] = [{ id: 'SEND_EMAIL', name: 'Send Email' }]

export default function CreateAutomationSidePanel(props: {
  onClose: () => void
  addConnection?: (from: number, to: number, label: string) => void
  setAutomationTitle?: React.Dispatch<React.SetStateAction<string>>
}) {
  const [open, setOpen] = useState(true)
  const { templates } = useTemplateContext()
  const queryClient = useQueryClient()
  const { addNotification } = useNotifications()

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<AutomationForm>({
    resolver: zodResolver(automationFormSchema),
    defaultValues: {
      name: '',
      triggerType: '',
      schedule: '',
      condition: {
        type: '',
        field: '',
        operation: '',
        value: '',
        unit: '',
      },
      action: {
        type: '',
        template: '',
      },
    },
  })

  const selectedTriggerType = watch('triggerType')
  const selectedConditionType = watch('condition.type')
  const selectedAction = watch('action.type')
  const filteredFields = conditionFields.filter(field => {
    switch (selectedConditionType) {
      case 'DATE':
        return field.type === 'DATE'
      case 'NUMERIC':
        return field.type === 'NUMERIC'
      case 'STRING':
        return field.type === 'STRING'
      default:
        return true
    }
  })
  const selectedField = conditionFields.find(
    field => field.id === watch('condition.field')
  )

  const handleOnClose = useCallback(() => {
    props.onClose()
  }, [props])

  const onSubmit = async (data: AutomationForm) => {
    try {
      // Transform form data to match backend schema
      const automationData = {
        name: data.name,
        trigger: {
          type: data.triggerType,
          schedule: data.schedule,
        },
        condition: [
          {
            type: data.condition.type,
            field: data.condition.field,
            operation: data.condition.operation,
            value: data.condition.value,
            unit: data.condition.unit,
          },
        ],
        action: {
          type: data.action.type,
          templateId: data.action.template,
        },
      }

      await DAIRequest('POST', '/api/v1/automations/createAutomation' as any, {
        body: automationData,
      })

      addNotification('success', 'Automation created successfully')
      await queryClient.invalidateQueries({ queryKey: ['automations'] })
      setOpen(false)
      handleOnClose()
    } catch (error) {
      console.error('Failed to create automation:', error)
      addNotification('error', 'Failed to create automation')
    }
  }

  console.log(selectedField)

  return (
    <DAISidePanel
      title={'Create Automation'}
      open={open}
      onClose={handleOnClose}
    >
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex h-full flex-col divide-y divide-gray-200 bg-white"
      >
        <div className="h-0 flex-1 overflow-y-auto">
          <div className="flex flex-1 flex-col justify-between">
            <div className="divide-y divide-gray-200 px-4">
              <div className="space-y-6 pt-6 pb-5">
                <DAITextBox
                  name="name"
                  label="Automation Name"
                  required
                  error={(errors as FormErrors).name?.message}
                  register={register('name')}
                />
                <DAIComboBox
                  name="triggerType"
                  label="Trigger Type"
                  data={triggerTypes}
                  required
                  error={(errors as FormErrors).triggerType?.message}
                  register={register('triggerType')}
                />
                {selectedTriggerType === 'TIME_BASED' && (
                  <DAITextBox
                    name="schedule"
                    label="Schedule (Cron Expression)"
                    required
                    error={(errors as FormErrors).schedule?.message}
                    register={register('schedule')}
                    placeholder="0 9 * * * (daily at 9 AM)"
                  />
                )}
                {selectedTriggerType === 'DATE_BASED' && (
                  <></>
                  // <DAIDateInput
                  //   name="schedule"
                  //   label="Schedule Date and Time"
                  //   required
                  //   error={(errors as FormErrors).schedule?.message}
                  // />
                )}
                <div className="space-y-6">
                  <h3 className="text-sm font-medium text-gray-900">
                    Condition
                  </h3>
                  <DAIComboBox
                    name="condition.type"
                    label="Condition Type"
                    data={conditionTypes}
                    required
                    error={(errors as FormErrors).condition?.type?.message}
                    register={register('condition.type')}
                  />
                  <DAIComboBox
                    name="condition.field"
                    label="Field"
                    data={filteredFields}
                    required
                    error={(errors as FormErrors).condition?.field?.message}
                    register={register('condition.field')}
                  />
                  <DAIComboBox
                    name="condition.operation"
                    label="Operation"
                    data={operations}
                    required
                    error={(errors as FormErrors).condition?.operation?.message}
                    register={register('condition.operation')}
                  />
                  {selectedField?.type === 'DATE' ? (
                    <></>
                  ) : // <DAIDateInput
                  //   name="condition.value"
                  //   label="Value"
                  //   required
                  //   error={(errors as FormErrors).condition?.value?.message}
                  // />
                  selectedField?.type === 'NUMERIC' ? (
                    <DAITextBox
                      name="condition.value"
                      label="Value"
                      type="number"
                      required
                      error={(errors as FormErrors).condition?.value?.message}
                      register={register('condition.value')}
                    />
                  ) : (
                    <DAITextBox
                      name="condition.value"
                      label="Value"
                      required
                      error={(errors as FormErrors).condition?.value?.message}
                      register={register('condition.value')}
                    />
                  )}
                  {selectedConditionType === 'DATE' && (
                    <DAIComboBox
                      name="condition.unit"
                      label="Unit"
                      data={units}
                      required
                      error={(errors as FormErrors).condition?.unit?.message}
                      register={register('condition.unit')}
                    />
                  )}
                </div>
                <div className="space-y-6">
                  <h3 className="text-sm font-medium text-gray-900">Action</h3>
                  <DAIComboBox
                    name="action.type"
                    label="Action Type"
                    data={actionTypes}
                    required
                    error={(errors as FormErrors).action?.type?.message}
                    register={register('action.type')}
                  />

                  {selectedAction === 'SEND_EMAIL' && (
                    <DAIComboBox
                      name="action.template"
                      label="Email Template"
                      data={templates.map(t => ({ id: t.id, name: t.name }))}
                      required
                      error={(errors as FormErrors).action?.template?.message}
                      register={register('action.template')}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-shrink-0 justify-end px-4 py-4">
          <button
            type="button"
            className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
            onClick={() => setOpen(false)}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="ml-4 inline-flex justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Creating...' : 'Create'}
          </button>
        </div>
      </form>
    </DAISidePanel>
  )
}
